import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Users, Building2, Globe, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

const leadership = [
  {
    name: "Dr. Robert Anderson",
    title: "Chief Executive Officer",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400",
    bio: "30+ years in healthcare leadership"
  },
  {
    name: "Dr. Jennifer Walsh",
    title: "Chief Medical Officer",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400",
    bio: "Former Director at Mayo Clinic"
  },
  {
    name: "Michael Chen",
    title: "Chief Operations Officer",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400",
    bio: "Healthcare operations expert"
  },
  {
    name: "Sarah Williams",
    title: "Chief Financial Officer",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400",
    bio: "20+ years healthcare finance"
  }
];

const values = [
  { icon: Users, title: "Patient-Centered", desc: "Every decision puts patients first" },
  { icon: Award, title: "Excellence", desc: "Highest standards of medical care" },
  { icon: Globe, title: "Accessibility", desc: "Healthcare for all communities" },
  { icon: Building2, title: "Innovation", desc: "Advancing medical technology" }
];

export default function About() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#F1FAEE] py-20 lg:py-28" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-1.5 bg-[#00B4D8]/10 text-[#00B4D8] text-sm font-medium rounded-full mb-6">
                About Us
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#14213D] leading-tight mb-6">
                America's Trusted
                <span className="text-[#00B4D8] block">Healthcare Network</span>
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                For over 25 years, Medical Access Inc has been connecting patients 
                with quality healthcare providers across the nation. Our mission is to make 
                excellent medical care accessible to every community.
              </p>
              <p className="text-gray-600 leading-relaxed">
                From primary care to specialized treatments, our network of board-certified 
                physicians and healthcare professionals delivers compassionate, patient-centered 
                care at every location.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800"
                alt="Medical team"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#14213D] rounded-2xl p-8 text-white"
            >
              <h3 className="font-heading text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-gray-300 leading-relaxed">
                To provide accessible, high-quality healthcare services through a nationwide 
                network of trusted medical professionals, ensuring every patient receives 
                the care they deserve regardless of their location.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#00B4D8] rounded-2xl p-8 text-white"
            >
              <h3 className="font-heading text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-white/90 leading-relaxed">
                To be the nation's most trusted healthcare network, setting the standard 
                for medical excellence, patient satisfaction, and innovative care delivery 
                across all communities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#F1FAEE]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Our Values</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 text-center card-hover"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 bg-[#00B4D8]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-[#00B4D8]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#14213D] mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Leadership</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2">
              Executive Team
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((leader, index) => (
              <motion.div
                key={leader.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`leader-${index}`}
              >
                <div className="headshot-container w-40 h-40 mx-auto mb-4">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#14213D]">
                  {leader.name}
                </h3>
                <p className="text-[#00B4D8] text-sm font-medium">{leader.title}</p>
                <p className="text-gray-500 text-sm mt-1">{leader.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers */}
      <section className="py-20 bg-[#14213D]" data-testid="numbers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              By the Numbers
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "25+", label: "Years of Service" },
              { value: "500K+", label: "Patients Served" },
              { value: "1,000+", label: "Healthcare Providers" },
              { value: "50", label: "States Covered" },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`number-${index}`}
              >
                <p className="font-heading text-4xl md:text-5xl font-bold text-[#00B4D8]">
                  {stat.value}
                </p>
                <p className="text-gray-400 mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2 mb-6">
                The Medical Access Difference
              </h2>
              <ul className="space-y-4">
                {[
                  "Board-certified physicians across all specialties",
                  "State-of-the-art medical facilities",
                  "Accepted by major insurance providers",
                  "Convenient online booking and telehealth",
                  "Coordinated care across our network",
                  "24/7 patient support services",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#00B4D8] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="inline-block mt-8">
                <Button
                  className="bg-[#14213D] text-white rounded-lg px-6 py-5"
                  data-testid="contact-us-btn"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800"
                alt="Modern medical facility"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#00B4D8]" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Experience Quality Healthcare?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Join the thousands of patients who trust Medical Access Inc for their healthcare needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/find-provider">
                <Button
                  className="bg-white text-[#00B4D8] rounded-lg px-6 py-5 hover:bg-gray-100"
                  data-testid="find-provider-btn"
                >
                  Find a Provider
                </Button>
              </Link>
              <Link to="/careers">
                <Button
                  variant="outline"
                  className="border-white text-white rounded-lg px-6 py-5 hover:bg-white/10"
                  data-testid="join-team-btn"
                >
                  Join Our Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
